import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Plus,
  Trash2,
  Link as LinkIcon,
  TrendingUp,
  Send,
  ExternalLink,
  Sparkles,
  Paperclip,
  Upload,
  AlertTriangle,
  CalendarClock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListDirectories,
  adminUpsertDirectory,
  adminDeleteDirectory,
  adminListBacklinkSnapshots,
  adminAddBacklinkSnapshot,
  adminDeleteBacklinkSnapshot,
  adminListPressMentions,
  adminCreatePressMention,
  adminUpdatePressMention,
  adminCreateProofUploadUrl,
  adminSignProofUrl,
  adminDeleteProof,
  adminListNextActions,
} from "@/lib/api/outreach.functions";
import { outreachTemplates } from "@/lib/outreach-templates";


export const Route = createFileRoute("/_authenticated/portal/admin/outreach")({
  component: AdminOutreachPage,
});

type Tab = "next" | "directories" | "backlinks" | "templates" | "press";

const STATUS_COLORS: Record<string, string> = {
  not_started: "bg-muted text-foreground",
  in_progress: "bg-amber-100 text-amber-800",
  submitted: "bg-blue-100 text-blue-800",
  listed: "bg-emerald-100 text-emerald-800",
  follow_up: "bg-purple-100 text-purple-800",
  rejected: "bg-rose-100 text-rose-800",
  draft: "bg-muted text-foreground",
  sent: "bg-blue-100 text-blue-800",
  responded: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  declined: "bg-rose-100 text-rose-800",
};

function copy(text: string, label = "Copied") {
  navigator.clipboard?.writeText(text).then(
    () => toast.success(label),
    () => toast.error("Copy failed"),
  );
}

function AdminOutreachPage() {
  const [tab, setTab] = useState<Tab>("next");
  return (
    <section className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">SEO growth</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Outreach & backlink hub</h2>
        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
          A pre-loaded list of South African legal & business directories, five
          ready-to-send outreach templates, a backlink dashboard, and a press /
          Google Business Profile submission drafter.
        </p>
      </header>

      <nav className="flex flex-wrap gap-2 border-b border-border">
        {(
          [
            ["next", "Next actions"],
            ["directories", "Directory tracker"],
            ["backlinks", "Backlink dashboard"],
            ["press", "Press & GBP forms"],
            ["templates", "Email templates"],
          ] as [Tab, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              tab === id
                ? "border-gold text-navy"
                : "border-transparent text-muted-foreground hover:text-navy"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "next" && <NextActionsPanel onJump={setTab} />}
      {tab === "directories" && <DirectoriesPanel />}
      {tab === "backlinks" && <BacklinksPanel />}
      {tab === "press" && <PressPanel />}
      {tab === "templates" && <TemplatesPanel />}
    </section>
  );
}

/* -------------------------------------------------------- Next actions */

type ProofRec = { path: string; name: string; mime?: string | null; size?: number | null; uploaded_at?: string };

function NextActionsPanel({ onJump }: { onJump: (t: Tab) => void }) {
  const list = useServerFn(adminListNextActions);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-outreach-next-actions"],
    queryFn: () => list(),
  });
  const today = data?.today ?? new Date().toISOString().slice(0, 10);
  const dirs = data?.directories ?? [];
  const press = data?.press ?? [];
  const total = dirs.length + press.length;

  const overdueCount =
    dirs.filter((r: any) => r.follow_up_at < today).length +
    press.filter((r: any) => r.follow_up_at < today).length;

  const daysDiff = (d: string) => {
    const a = new Date(today + "T00:00:00Z").getTime();
    const b = new Date(d + "T00:00:00Z").getTime();
    return Math.round((b - a) / 86400000);
  };
  const dueLabel = (d: string) => {
    const n = daysDiff(d);
    if (n === 0) return { text: "Due today", overdue: false };
    if (n < 0) return { text: `${Math.abs(n)}d overdue`, overdue: true };
    return { text: `in ${n}d`, overdue: false };
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Total open</p>
          <p className="mt-1 font-display text-2xl text-navy">{total}</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Overdue</p>
          <p className={`mt-1 font-display text-2xl ${overdueCount ? "text-rose-600" : "text-navy"}`}>
            {overdueCount}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">As of</p>
          <p className="mt-1 font-display text-2xl text-navy">{today}</p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : total === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          <CalendarClock className="mx-auto h-6 w-6 text-gold" />
          <p className="mt-2">Nothing due today or overdue — you're clear. Set follow-up dates on directories or press mentions to see them here.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <h3 className="font-display text-navy">Directory follow-ups ({dirs.length})</h3>
              <button
                onClick={() => onJump("directories")}
                className="text-xs text-muted-foreground hover:text-navy"
              >
                Open tracker →
              </button>
            </div>
            <ul className="divide-y divide-border">
              {dirs.length === 0 && (
                <li className="p-4 text-sm text-muted-foreground">All directories are on schedule.</li>
              )}
              {dirs.map((r: any) => {
                const d = dueLabel(r.follow_up_at);
                return (
                  <li key={r.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold ${
                        d.overdue ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {d.overdue && <AlertTriangle className="h-3 w-3" />} {d.text}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-navy">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.follow_up_at} ·{" "}
                        <span className={`rounded px-1.5 py-0.5 ${STATUS_COLORS[r.status] ?? ""}`}>
                          {r.status}
                        </span>
                      </p>
                    </div>
                    <a
                      href={r.submission_url || r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded border border-border px-2 py-1 text-xs hover:border-navy"
                    >
                      Open
                    </a>
                    {r.contact_email && (
                      <a
                        href={`mailto:${r.contact_email}`}
                        className="rounded bg-gold px-2 py-1 text-xs font-semibold text-gold-foreground"
                      >
                        Email
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <h3 className="font-display text-navy">Press & GBP follow-ups ({press.length})</h3>
              <button
                onClick={() => onJump("press")}
                className="text-xs text-muted-foreground hover:text-navy"
              >
                Open forms →
              </button>
            </div>
            <ul className="divide-y divide-border">
              {press.length === 0 && (
                <li className="p-4 text-sm text-muted-foreground">No press or GBP items due.</li>
              )}
              {press.map((r: any) => {
                const d = dueLabel(r.follow_up_at);
                return (
                  <li key={r.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold ${
                        d.overdue ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {d.overdue && <AlertTriangle className="h-3 w-3" />} {d.text}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-navy">
                        <span className="mr-1 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">
                          {r.kind}
                        </span>
                        {r.outlet_name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {r.angle} · {r.follow_up_at} ·{" "}
                        <span className={`rounded px-1.5 py-0.5 ${STATUS_COLORS[r.status] ?? ""}`}>
                          {r.status}
                        </span>
                      </p>
                    </div>
                    {r.contact_email && (
                      <a
                        href={`mailto:${r.contact_email}`}
                        className="rounded bg-gold px-2 py-1 text-xs font-semibold text-gold-foreground"
                      >
                        Email
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <div>
        <button
          onClick={() => refetch()}
          className="rounded border border-border px-3 py-1.5 text-xs hover:border-navy"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- Proofs uploader */

function ProofsBlock({
  scope,
  recordId,
  proofs,
  onChange,
}: {
  scope: "directory" | "press";
  recordId: string;
  proofs: ProofRec[];
  onChange: (next: ProofRec[]) => Promise<void> | void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const signUpload = useServerFn(adminCreateProofUploadUrl);
  const signView = useServerFn(adminSignProofUrl);
  const del = useServerFn(adminDeleteProof);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const uploaded: ProofRec[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 20MB`);
          continue;
        }
        const { path, token } = await signUpload({
          data: { scope, record_id: recordId, filename: file.name },
        });
        const up = await supabase.storage
          .from("outreach-proofs")
          .uploadToSignedUrl(path, token, file);
        if (up.error) {
          toast.error(`Upload failed: ${file.name}`);
          continue;
        }
        uploaded.push({
          path,
          name: file.name,
          mime: file.type || null,
          size: file.size,
          uploaded_at: new Date().toISOString(),
        });
      }
      if (uploaded.length) {
        await onChange([...proofs, ...uploaded]);
        toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} attached`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function openProof(path: string) {
    try {
      const { url } = await signView({ data: { path } });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e?.message || "Could not open");
    }
  }

  async function removeProof(p: ProofRec) {
    if (!confirm(`Remove ${p.name}?`)) return;
    try {
      const res = await del({ data: { scope, record_id: recordId, path: p.path } });
      await onChange(res.proofs as ProofRec[]);
    } catch (e: any) {
      toast.error(e?.message || "Delete failed");
    }
  }

  return (
    <div className="rounded border border-dashed border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Paperclip className="mr-1 inline h-3 w-3" /> Proof attachments ({proofs.length})
        </p>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs hover:border-navy">
          <Upload className="h-3 w-3" /> {busy ? "Uploading…" : "Add files"}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.eml,.msg,.txt,.html"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={busy}
          />
        </label>
      </div>
      {proofs.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Attach email confirmations (PDF, .eml) or screenshots to keep the paper trail.
        </p>
      ) : (
        <ul className="mt-2 space-y-1">
          {proofs.map((p) => (
            <li
              key={p.path}
              className="flex items-center gap-2 rounded bg-background px-2 py-1 text-xs"
            >
              <button
                onClick={() => openProof(p.path)}
                className="min-w-0 flex-1 truncate text-left text-navy hover:underline"
              >
                {p.name}
              </button>
              <span className="text-muted-foreground">
                {p.size ? `${Math.round(p.size / 1024)} KB` : ""}
              </span>
              <button
                onClick={() => removeProof(p)}
                className="rounded border border-border p-1 text-rose-600 hover:border-rose-500"
                title="Remove"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* -------------------------------------------------------- Directories */

function DirectoriesPanel() {
  const qc = useQueryClient();
  const list = useServerFn(adminListDirectories);
  const upsert = useServerFn(adminUpsertDirectory);
  const del = useServerFn(adminDeleteDirectory);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-outreach-directories"],
    queryFn: () => list(),
  });
  const rows: any[] = data?.directories ?? [];

  const [filter, setFilter] = useState<string>("all");
  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const stats = useMemo(() => {
    const by: Record<string, number> = {};
    rows.forEach((r) => (by[r.status] = (by[r.status] ?? 0) + 1));
    return by;
  }, [rows]);

  const patch = useMutation({
    mutationFn: (payload: any) => upsert({ data: payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-outreach-directories"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["admin-outreach-directories"] });
    },
  });

  const [editing, setEditing] = useState<any | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["all", `All (${rows.length})`],
          ["not_started", `Not started (${stats.not_started ?? 0})`],
          ["in_progress", `In progress (${stats.in_progress ?? 0})`],
          ["submitted", `Submitted (${stats.submitted ?? 0})`],
          ["listed", `Listed (${stats.listed ?? 0})`],
          ["follow_up", `Follow-up (${stats.follow_up ?? 0})`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k as string)}
            className={`rounded-full border px-3 py-1 text-xs ${
              filter === k
                ? "border-navy bg-navy text-navy-foreground"
                : "border-border bg-background text-foreground hover:border-navy"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setEditing({ priority: 3, status: "not_started" })}
          className="ml-auto inline-flex items-center gap-1 rounded-md bg-gold px-3 py-1.5 text-xs font-semibold text-gold-foreground"
        >
          <Plus className="h-3.5 w-3.5" /> Add directory
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Directory</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Submitted</th>
                <th className="px-3 py-2">Follow-up</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-medium text-navy">{r.name}</div>
                    <a
                      href={r.submission_url || r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
                    >
                      <LinkIcon className="h-3 w-3" /> {r.url}
                    </a>
                    {r.notes && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {r.notes}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.category}</td>
                  <td className="px-3 py-2">
                    <select
                      value={r.status}
                      onChange={(e) =>
                        patch.mutate({
                          id: r.id,
                          name: r.name,
                          category: r.category,
                          url: r.url,
                          status: e.target.value as any,
                          submitted_at:
                            e.target.value === "submitted" && !r.submitted_at
                              ? new Date().toISOString()
                              : r.submitted_at,
                        })
                      }
                      className={`rounded px-2 py-1 text-xs ${STATUS_COLORS[r.status] ?? ""}`}
                    >
                      <option value="not_started">Not started</option>
                      <option value="in_progress">In progress</option>
                      <option value="submitted">Submitted</option>
                      <option value="listed">Listed</option>
                      <option value="follow_up">Follow-up</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      value={r.follow_up_at ?? ""}
                      onChange={(e) =>
                        patch.mutate({
                          id: r.id,
                          name: r.name,
                          category: r.category,
                          url: r.url,
                          follow_up_at: e.target.value || null,
                        })
                      }
                      className="rounded border border-input bg-background px-2 py-1 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        className="rounded border border-border px-2 py-1 text-xs hover:border-navy"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete ${r.name}?`)) remove.mutate(r.id);
                        }}
                        className="rounded border border-border px-2 py-1 text-xs text-rose-600 hover:border-rose-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No directories match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <DirectoryEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await patch.mutateAsync(payload);
            setEditing(null);
            toast.success("Saved");
          }}
        />
      )}
    </div>
  );
}

function DirectoryEditor({
  initial,
  onSave,
  onClose,
}: {
  initial: any;
  onSave: (p: any) => Promise<void>;
  onClose: () => void;
}) {
  const upsert = useServerFn(adminUpsertDirectory);
  const [proofs, setProofs] = useState<ProofRec[]>(
    Array.isArray(initial.proofs) ? (initial.proofs as ProofRec[]) : [],
  );
  const [form, setForm] = useState<any>({
    id: initial.id,
    name: initial.name ?? "",
    category: initial.category ?? "Legal directory",
    url: initial.url ?? "",
    submission_url: initial.submission_url ?? "",
    contact_email: initial.contact_email ?? "",
    notes: initial.notes ?? "",
    priority: initial.priority ?? 3,
    status: initial.status ?? "not_started",
    live_url: initial.live_url ?? "",
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-background p-5 shadow-lifted">
        <h3 className="font-display text-lg text-navy">
          {initial.id ? "Edit directory" : "Add directory"}
        </h3>
        <div className="mt-4 space-y-3 text-sm">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Category">
            <input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Website URL">
            <input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="input"
              type="url"
            />
          </Field>
          <Field label="Submission / contact URL">
            <input
              value={form.submission_url ?? ""}
              onChange={(e) => setForm({ ...form, submission_url: e.target.value })}
              className="input"
              type="url"
            />
          </Field>
          <Field label="Contact email">
            <input
              value={form.contact_email ?? ""}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="input"
              type="email"
            />
          </Field>
          <Field label="Live listing URL (once approved)">
            <input
              value={form.live_url ?? ""}
              onChange={(e) => setForm({ ...form, live_url: e.target.value })}
              className="input"
              type="url"
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input h-20"
            />
          </Field>
          {form.id ? (
            <ProofsBlock
              scope="directory"
              recordId={form.id}
              proofs={proofs}
              onChange={async (next) => {
                setProofs(next);
                await upsert({
                  data: {
                    id: form.id,
                    name: form.name,
                    category: form.category,
                    url: form.url,
                    proofs: next,
                  },
                });
              }}
            />
          ) : (
            <p className="rounded border border-dashed border-border bg-muted/30 p-2 text-xs text-muted-foreground">
              Save this directory first to attach proof files.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority (1 high – 5 low)">
              <input
                type="number"
                min={1}
                max={5}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                className="input"
              />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="input"
              >
                <option value="not_started">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="submitted">Submitted</option>
                <option value="listed">Listed</option>
                <option value="follow_up">Follow-up</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const payload: any = { ...form };
              if (!payload.id) delete payload.id;
              if (!payload.submission_url) payload.submission_url = null;
              if (!payload.contact_email) payload.contact_email = null;
              if (!payload.live_url) payload.live_url = null;
              onSave(payload);
            }}
            className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-navy-foreground"
          >
            Save
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid hsl(var(--input));border-radius:6px;padding:6px 8px;background:hsl(var(--background));font-size:.875rem}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

/* -------------------------------------------------------- Backlinks */

function BacklinksPanel() {
  const qc = useQueryClient();
  const list = useServerFn(adminListBacklinkSnapshots);
  const add = useServerFn(adminAddBacklinkSnapshot);
  const del = useServerFn(adminDeleteBacklinkSnapshot);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-backlink-snapshots"],
    queryFn: () => list(),
  });
  const snapshots: any[] = data?.snapshots ?? [];
  const latest = snapshots[snapshots.length - 1];
  const previous = snapshots[snapshots.length - 2];

  const delta = (field: string) =>
    latest && previous ? (latest[field] ?? 0) - (previous[field] ?? 0) : 0;

  const [form, setForm] = useState({
    referring_domains: latest?.referring_domains ?? 14,
    total_backlinks: latest?.total_backlinks ?? 15,
    authority_score: latest?.authority_score ?? 2,
    trust_score: latest?.trust_score ?? 2,
    notes: "",
  });

  const addMut = useMutation({
    mutationFn: () => add({ data: { ...form, source: "manual" } }),
    onSuccess: () => {
      toast.success("Snapshot added");
      setForm({ ...form, notes: "" });
      qc.invalidateQueries({ queryKey: ["admin-backlink-snapshots"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard
          label="Referring domains"
          value={latest?.referring_domains ?? 0}
          delta={delta("referring_domains")}
        />
        <StatCard
          label="Total backlinks"
          value={latest?.total_backlinks ?? 0}
          delta={delta("total_backlinks")}
        />
        <StatCard
          label="Authority score"
          value={latest?.authority_score ?? 0}
          delta={delta("authority_score")}
          suffix="/100"
        />
        <StatCard
          label="Trust score"
          value={latest?.trust_score ?? 0}
          delta={delta("trust_score")}
          suffix="/100"
        />
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-gold" />
          <h3 className="font-display text-navy">Over time</h3>
        </div>
        <SparkChart snapshots={snapshots} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="font-display text-navy">Log a snapshot</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Paste the latest numbers from Semrush, Ahrefs, or Google Search Console (Links report).
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            {(
              [
                ["referring_domains", "Referring domains"],
                ["total_backlinks", "Total backlinks"],
                ["authority_score", "Authority score"],
                ["trust_score", "Trust score"],
              ] as const
            ).map(([k, label]) => (
              <Field key={k} label={label}>
                <input
                  type="number"
                  min={0}
                  max={k.includes("score") ? 100 : 1000000}
                  value={(form as any)[k]}
                  onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
                  className="w-full rounded border border-input bg-background px-2 py-1"
                />
              </Field>
            ))}
          </div>
          <Field label="Notes (optional)">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-2 h-16 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            />
          </Field>
          <button
            onClick={() => addMut.mutate()}
            disabled={addMut.isPending}
            className="mt-3 inline-flex items-center gap-1 rounded bg-navy px-3 py-1.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
          >
            <Plus className="h-3.5 w-3.5" /> Save snapshot
          </button>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="font-display text-navy">History</h3>
          <div className="mt-2 max-h-72 overflow-y-auto">
            {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {snapshots.length === 0 && !isLoading && (
              <p className="text-sm text-muted-foreground">No snapshots yet.</p>
            )}
            <ul className="divide-y divide-border text-sm">
              {[...snapshots].reverse().map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-navy">
                      {new Date(s.captured_at).toLocaleDateString()}{" "}
                      <span className="text-xs text-muted-foreground">
                        · {s.referring_domains} RD · {s.total_backlinks} links · AS {s.authority_score}
                      </span>
                    </p>
                    {s.notes && (
                      <p className="text-xs text-muted-foreground">{s.notes}</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (confirm("Delete snapshot?"))
                        del({ data: { id: s.id } }).then(() =>
                          qc.invalidateQueries({ queryKey: ["admin-backlink-snapshots"] }),
                        );
                    }}
                    className="rounded border border-border p-1 text-xs text-rose-600 hover:border-rose-500"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  delta,
  suffix,
}: {
  label: string;
  value: number;
  delta: number;
  suffix?: string;
}) {
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const color =
    delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-muted-foreground";
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl text-navy">
        {value}
        {suffix ?? ""}
      </p>
      <p className={`mt-1 text-xs ${color}`}>
        {arrow} {Math.abs(delta)} vs previous
      </p>
    </div>
  );
}

function SparkChart({ snapshots }: { snapshots: any[] }) {
  if (snapshots.length < 2) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        Log at least two snapshots to see a trend line.
      </p>
    );
  }
  const width = 640;
  const height = 160;
  const pad = 24;
  const xs = snapshots.map((_, i) => i);
  const series = [
    { key: "referring_domains", color: "#0f3460", label: "Referring domains" },
    { key: "total_backlinks", color: "#c8963e", label: "Backlinks" },
    { key: "authority_score", color: "#059669", label: "Authority" },
  ];
  const maxY = Math.max(
    ...snapshots.flatMap((s) =>
      series.map((sr) => Number(s[sr.key] ?? 0)),
    ),
    1,
  );
  const scaleX = (i: number) =>
    pad + (i / Math.max(xs.length - 1, 1)) * (width - pad * 2);
  const scaleY = (v: number) => height - pad - (v / maxY) * (height - pad * 2);
  return (
    <div className="mt-3 overflow-x-auto">
      <svg width={width} height={height} className="max-w-full">
        {series.map((sr) => {
          const d = snapshots
            .map((s, i) => `${i === 0 ? "M" : "L"} ${scaleX(i)} ${scaleY(Number(s[sr.key] ?? 0))}`)
            .join(" ");
          return (
            <path
              key={sr.key}
              d={d}
              fill="none"
              stroke={sr.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="h-2 w-4 rounded" style={{ background: s.color }} /> {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- Press / GBP */

function PressPanel() {
  const qc = useQueryClient();
  const list = useServerFn(adminListPressMentions);
  const create = useServerFn(adminCreatePressMention);
  const update = useServerFn(adminUpdatePressMention);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-press-mentions"],
    queryFn: () => list(),
  });
  const mentions: any[] = data?.mentions ?? [];

  const [form, setForm] = useState<any>({
    kind: "press",
    outlet_name: "",
    outlet_url: "",
    contact_name: "",
    contact_email: "",
    angle: "",
    key_points: "",
    target_audience: "",
    gbp_update_type: "",
  });

  const createMut = useMutation({
    mutationFn: () =>
      create({
        data: {
          ...form,
          outlet_url: form.outlet_url || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          gbp_update_type: form.gbp_update_type || null,
        },
      }),
    onSuccess: () => {
      toast.success("Draft generated");
      setForm({ ...form, angle: "", key_points: "" });
      qc.invalidateQueries({ queryKey: ["admin-press-mentions"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="font-display text-navy">New submission</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Fill in the details — an outreach email draft is generated automatically.
        </p>
        <div className="mt-3 space-y-3 text-sm">
          <Field label="Type">
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full rounded border border-input bg-background px-2 py-1"
            >
              <option value="press">Press mention pitch</option>
              <option value="gbp">Google Business Profile update</option>
            </select>
          </Field>
          <Field label={form.kind === "gbp" ? "Team / recipient" : "Outlet / publication"}>
            <input
              value={form.outlet_name}
              onChange={(e) => setForm({ ...form, outlet_name: e.target.value })}
              className="w-full rounded border border-input bg-background px-2 py-1"
            />
          </Field>
          {form.kind === "gbp" && (
            <Field label="Update type (e.g. new hours, new service, new photo)">
              <input
                value={form.gbp_update_type}
                onChange={(e) => setForm({ ...form, gbp_update_type: e.target.value })}
                className="w-full rounded border border-input bg-background px-2 py-1"
              />
            </Field>
          )}
          <Field label="Outlet website (optional)">
            <input
              value={form.outlet_url}
              onChange={(e) => setForm({ ...form, outlet_url: e.target.value })}
              className="w-full rounded border border-input bg-background px-2 py-1"
              type="url"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact name">
              <input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="w-full rounded border border-input bg-background px-2 py-1"
              />
            </Field>
            <Field label="Contact email">
              <input
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full rounded border border-input bg-background px-2 py-1"
                type="email"
              />
            </Field>
          </div>
          <Field label={form.kind === "gbp" ? "Short summary" : "Angle / headline"}>
            <input
              value={form.angle}
              onChange={(e) => setForm({ ...form, angle: e.target.value })}
              className="w-full rounded border border-input bg-background px-2 py-1"
              placeholder={
                form.kind === "gbp"
                  ? "e.g. Updated Saturday hours"
                  : "e.g. Local paralegal launches free CCMA checklist"
              }
            />
          </Field>
          <Field label="Key points / details">
            <textarea
              value={form.key_points}
              onChange={(e) => setForm({ ...form, key_points: e.target.value })}
              className="h-24 w-full rounded border border-input bg-background px-2 py-1"
            />
          </Field>
          {form.kind === "press" && (
            <Field label="Target audience / topic area">
              <input
                value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                className="w-full rounded border border-input bg-background px-2 py-1"
                placeholder="e.g. HR professionals, SME owners"
              />
            </Field>
          )}
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !form.outlet_name || !form.angle}
            className="inline-flex items-center gap-1 rounded bg-navy px-3 py-1.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
          >
            <Sparkles className="h-3.5 w-3.5" /> Draft email
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="font-display text-navy">Recent drafts</h3>
        {isLoading && <p className="mt-2 text-sm text-muted-foreground">Loading…</p>}
        {mentions.length === 0 && !isLoading && (
          <p className="mt-2 text-sm text-muted-foreground">No drafts yet.</p>
        )}
        <div className="mt-3 max-h-[520px] space-y-3 overflow-y-auto">
          {mentions.map((m) => (
            <details key={m.id} className="rounded border border-border p-3 text-sm">
              <summary className="cursor-pointer">
                <span
                  className={`mr-2 rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${
                    m.kind === "gbp"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {m.kind}
                </span>
                <span className="font-medium text-navy">{m.outlet_name}</span>{" "}
                <span className="text-xs text-muted-foreground">— {m.angle}</span>
                <span
                  className={`ml-2 rounded px-2 py-0.5 text-[10px] ${
                    STATUS_COLORS[m.status] ?? ""
                  }`}
                >
                  {m.status}
                </span>
              </summary>
              <div className="mt-3 space-y-3">
                <textarea
                  defaultValue={m.drafted_email}
                  onBlur={(e) =>
                    e.target.value !== m.drafted_email &&
                    update({ data: { id: m.id, drafted_email: e.target.value } })
                  }
                  className="h-56 w-full rounded border border-input bg-background p-2 text-xs font-mono"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => copy(m.drafted_email, "Email copied")}
                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-navy"
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                  {m.contact_email && (
                    <a
                      href={`mailto:${m.contact_email}?subject=${encodeURIComponent(
                        (m.drafted_email.split("\n")[0] || "").replace(/^Subject:\s*/, ""),
                      )}&body=${encodeURIComponent(
                        m.drafted_email.split("\n").slice(2).join("\n"),
                      )}`}
                      className="inline-flex items-center gap-1 rounded bg-gold px-2 py-1 text-xs font-semibold text-gold-foreground"
                    >
                      <Send className="h-3 w-3" /> Open in email
                    </a>
                  )}
                  <select
                    defaultValue={m.status}
                    onChange={(e) =>
                      update({
                        data: {
                          id: m.id,
                          status: e.target.value as any,
                          sent_at:
                            e.target.value === "sent" ? new Date().toISOString() : m.sent_at,
                        },
                      }).then(() =>
                        qc.invalidateQueries({ queryKey: ["admin-press-mentions"] }),
                      )
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-xs"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="responded">Responded</option>
                    <option value="published">Published</option>
                    <option value="declined">Declined</option>
                  </select>
                  <input
                    type="date"
                    defaultValue={m.follow_up_at ?? ""}
                    onChange={(e) =>
                      update({
                        data: { id: m.id, follow_up_at: e.target.value || null },
                      })
                    }
                    className="rounded border border-input bg-background px-2 py-1 text-xs"
                  />
                  {m.outlet_url && (
                    <a
                      href={m.outlet_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
                    >
                      <ExternalLink className="h-3 w-3" /> Outlet
                    </a>
                  )}
                </div>
                <ProofsBlock
                  scope="press"
                  recordId={m.id}
                  proofs={Array.isArray(m.proofs) ? (m.proofs as ProofRec[]) : []}
                  onChange={async (next) => {
                    await update({ data: { id: m.id, proofs: next } });
                    qc.invalidateQueries({ queryKey: ["admin-press-mentions"] });
                  }}
                />
              </div>

            </details>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- Templates */

function TemplatesPanel() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {outreachTemplates.map((t) => (
        <article key={t.id} className="rounded-lg border border-border bg-card p-4">
          <header>
            <p className="text-[11px] uppercase tracking-wide text-gold">{t.audience}</p>
            <h3 className="mt-1 font-display text-navy">{t.title}</h3>
            <p className="mt-1 text-xs text-muted-foreground">Subject: {t.subject}</p>
          </header>
          <pre className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded bg-muted/50 p-3 text-xs">
            {t.body}
          </pre>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => copy(`Subject: ${t.subject}\n\n${t.body}`, "Template copied")}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-navy"
            >
              <Copy className="h-3 w-3" /> Copy full email
            </button>
            <button
              onClick={() => copy(t.subject, "Subject copied")}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-navy"
            >
              <Copy className="h-3 w-3" /> Subject only
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
